import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { MapAllowedIcaosService } from '../../../services/map-allowed-icaos.service';
import { MapAllowedIcao } from '../../../models/map-allowed-icao';
import { SnackBarService } from '../../../ui-services/snackbar/snackBar.service';
import { DeleteConfirmationComponent } from '../../../shared/components/delete-confirmation/delete-confirmation.component';
import { MapAllowedIcaosDialogNewComponent } from '../map-allowed-icaos-dialog-new/map-allowed-icaos-dialog-new.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

@Component({
    selector: 'app-map-allowed-icaos-home',
    templateUrl: './map-allowed-icaos-home.component.html',
    styleUrls: ['./map-allowed-icaos-home.component.scss'],
})
export class MapAllowedIcaosHomeComponent implements OnInit {
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    public pageTitle = '1200.aero Allowed ICAOs';
    public loaderName = 'map-allowed-icaos-loader';
    public searchFilter = '';
    public dataSource: MatTableDataSource<MapAllowedIcao>;
    public displayedColumns: string[] = ['icao', 'actions'];

    constructor(
        private ngxLoader: NgxUiLoaderService,
        private mapAllowedIcaosService: MapAllowedIcaosService,
        private snackbarService: SnackBarService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    public applyFilter(): void {
        if (this.dataSource) {
            this.dataSource.filter = this.searchFilter.trim().toLowerCase();
        }
    }

    public clearSearch(): void {
        this.searchFilter = '';
        this.applyFilter();
    }

    private loadData(): void {
        this.ngxLoader.startLoader(this.loaderName);
        this.mapAllowedIcaosService.getAll().subscribe({
            next: (data) => {
                const sortedData = data.sort((a, b) => a.icao.localeCompare(b.icao));
                this.dataSource = new MatTableDataSource(sortedData);
                this.dataSource.sort = this.sort;
                this.dataSource.filter = this.searchFilter.trim().toLowerCase();
                this.ngxLoader.stopLoader(this.loaderName);
            },
            error: (err) => {
                console.error(err);
                this.snackbarService.showErrorSnackBar('Failed to load allowed ICAOs');
                this.ngxLoader.stopLoader(this.loaderName);
            },
        });
    }

    public openAddDialog(): void {
        const dialogRef = this.dialog.open(MapAllowedIcaosDialogNewComponent, {
            width: '400px',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.addIcaos(result);
            }
        });
    }

    private addIcaos(icaosInput: string): void {
        this.ngxLoader.startLoader(this.loaderName);
        this.mapAllowedIcaosService.create(icaosInput).subscribe({
            next: (response) => {
                const messages: string[] = [];

                if (response.totalCreated > 0) {
                    messages.push(`Created: ${response.created.map((i) => i.icao).join(', ')}`);
                }
                if (response.duplicates.length > 0) {
                    messages.push(`Duplicates: ${response.duplicates.join(', ')}`);
                }
                if (response.invalid.length > 0) {
                    messages.push(`Invalid: ${response.invalid.join(', ')}`);
                }

                if (response.totalCreated > 0) {
                    this.snackbarService.showSuccessSnackBar(messages.join(' | '));
                } else {
                    this.snackbarService.showErrorSnackBar(messages.join(' | '));
                }

                this.loadData();
            },
            error: (err) => {
                console.error(err);
                this.snackbarService.showErrorSnackBar('Failed to add ICAOs');
                this.ngxLoader.stopLoader(this.loaderName);
            },
        });
    }

    public removeIcao(icao: MapAllowedIcao): void {
        const dialogRef = this.dialog.open(DeleteConfirmationComponent, {
            autoFocus: false,
            data: { description: 'ICAO', item: icao },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.ngxLoader.startLoader(this.loaderName);
            this.mapAllowedIcaosService.remove(icao.icao).subscribe({
                next: (response) => {
                    if (response.totalRemoved > 0) {
                        this.snackbarService.showSuccessSnackBar(`Removed: ${response.removed.join(', ')}`);
                    } else {
                        this.snackbarService.showErrorSnackBar(`Not found: ${response.notFound.join(', ')}`);
                    }
                    this.loadData();
                },
                error: (err) => {
                    console.error(err);
                    this.snackbarService.showErrorSnackBar('Failed to remove ICAO');
                    this.ngxLoader.stopLoader(this.loaderName);
                },
            });
        });
    }
}
